import React, { useEffect, useMemo, useState } from 'react';
import { RemoteResult, RemoteResultConfiguration } from '../../index.types';
import RemoteResultHelper from '../../../../../helper/RemoteResultHelper';
import { OutcomeType } from '../../remoteResultOutcome';
import { ConfirmContentType, ConfirmDialog } from '../../../../../components/envago/ConfirmDialog';
import Button from '../../../../../components/envago/Button/Button';
import MeterHelper from '../../../../../helper/MeterHelper';
import { HorizontalBase64Image } from '../../../../../components/envago/HorizontalBase64Image';
import { useTranslation } from 'react-i18next';
import { RemoteResultImageSource, resolveRemoteResultAiButtonTextKey, resolveRemoteResultAiTextKey } from '../../remoteResultImageSource';
import { RemoteResultMetaData } from '../../../../../redux/jobs/jobs.types';

interface RemoteResultOutcomeGateProps {
    config: RemoteResultConfiguration;
    result: RemoteResult | undefined;
    imageBase64: string;
    imageSource?: RemoteResultImageSource;
    onConfirm: (result: RemoteResult | undefined, imageBase64: string) => void;
    onRetakeImage: () => void;
    onCancel: () => void;
}

const RemoteResultOutcomeGate = ({
    config,
    result,
    imageBase64,
    imageSource = 'gallery',
    onConfirm,
    onRetakeImage,
    onCancel,
}: RemoteResultOutcomeGateProps) => {
    const { t } = useTranslation();
    const [payloadResult, setPayloadResult] = useState<RemoteResult | undefined | null>(null);

    useEffect(() => {
        let active = true;

        (async () => {
            const resultWithCrops = await RemoteResultHelper.enrichResultWithDisplayCrops(result, imageBase64);

            if (!active) {
                return;
            }

            setPayloadResult(resultWithCrops ?? undefined);
        })();

        return () => {
            active = false;
        };
    }, [result, imageBase64]);

    const outcome = useMemo<OutcomeType>(
        () => RemoteResultHelper.resolveRemoteResultOutcome(payloadResult ?? undefined, config),
        [payloadResult, config],
    );

    const expectedObis = config?.sourceMeterRate?.obis;

    // What the job expected vs. what the AI read, plus the resolved outcome as flags. The job
    // result stores this alongside the reading, so a later reviewer can tell an accepted warning
    // apart from a clean recognition.
    const meta = useMemo<RemoteResultMetaData>(() => {
        const resultMeta = payloadResult?.meta;

        return {
            expectedMeterNumber: config.meterNumber,
            detectedMeterNumber: resultMeta?.detectedMeterNumber,
            expectedObis: expectedObis,
            detectedObis: resultMeta?.detectedObis,
            expectedReadingValue: undefined,
            detectedReadingValue: resultMeta?.detectedReadingValue,
            expectedMedium: RemoteResultHelper.resolveMeterMediumByObis(expectedObis),
            detectedMedium: resultMeta?.detectedMedium,
            warnings: {
                VALID: outcome === 'VALID',
                NO_RESULT: outcome === 'NO_RESULT',
                INVALID_MEDIUM: outcome === 'INVALID_MEDIUM',
                INVALID_METER_NUMBER: outcome === 'INVALID_METER_NUMBER',
                INVALID_OBIS: outcome === 'INVALID_OBIS',
            },
        };
    }, [outcome, payloadResult, config.meterNumber, expectedObis]);

    useEffect(() => {
        // Until the crops have been enriched there is nothing to confirm yet.
        if (payloadResult === null) {
            return;
        }

        if (outcome === 'VALID' && payloadResult != null) {
            onConfirm({ ...RemoteResultHelper.applyExpectedObisToRates(payloadResult, expectedObis), meta }, imageBase64);
        } else if (outcome === 'NO_RESULT') {
            onConfirm(undefined, imageBase64);
        }
    }, [outcome, payloadResult, imageBase64, onConfirm, meta]);

    if (payloadResult == null) {
        return null;
    }

    const configRate = config?.sourceMeterRate;

    switch (outcome) {
        case 'INVALID_MEDIUM': {
            const expected = t(`common.metertypes.${RemoteResultHelper.resolveMeterTypeByMedium(RemoteResultHelper.resolveMeterMediumByObis(configRate?.obis))}`);
            const detected = t(`common.metertypes.${RemoteResultHelper.resolveMeterTypeByMedium(payloadResult?.medium)}`);

            return (
                <ConfirmDialog
                    closable={false}
                    open={true}
                    showIcon={true}
                    type={ConfirmContentType.WARNING}
                    onCancel={() => onCancel()}
                    title={t(resolveRemoteResultAiTextKey(imageSource, 'invalidMediumOutcome', 'title'))}
                    content={
                        <div className={'text-main'}>
                            <div>{t(resolveRemoteResultAiTextKey(imageSource, 'invalidMediumOutcome', 'description'))}</div>
                        </div>
                    }
                    customContent={
                        <div className={'text-main'}>
                            <table className="border-collapse w-full text-inherit">
                                <tbody>
                                    <tr>
                                        <td className="pr-2 text-inherit align-text-top">
                                            {t(resolveRemoteResultAiTextKey(imageSource, 'invalidMediumOutcome', 'expectedLabel'))}
                                        </td>
                                        <td className="text-inherit">
                                            <div>{expected || '-'}</div>
                                            <div>{config.meterNumber}</div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="pr-2 text-inherit align-text-top">
                                            {t(resolveRemoteResultAiTextKey(imageSource, 'invalidMediumOutcome', 'detectedLabel'))}
                                        </td>
                                        <td className="text-inherit">
                                            <div>{detected || '-'}</div>
                                            {payloadResult?.meterNumber && <div>{payloadResult?.meterNumber}</div>}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    }
                    customActions={[
                        <div className={'flex flex-col w-full'} key={'custom-actions'}>
                            <Button ghost={true} block onClick={() => onCancel()}>
                                {t('ai.buttons.cancel')}
                            </Button>
                            <Button
                                ghost={true}
                                block
                                onClick={() => onConfirm({ ...RemoteResultHelper.buildForcedResult(payloadResult, config, configRate?.obis), meta }, imageBase64)}
                            >
                                {t(resolveRemoteResultAiButtonTextKey(imageSource, 'takeImage'))}
                            </Button>
                            <Button primary block onClick={() => onRetakeImage()}>
                                {t(resolveRemoteResultAiButtonTextKey(imageSource, 'retakeImage'))}
                            </Button>
                        </div>,
                    ]}
                />
            );
        }
        case 'INVALID_METER_NUMBER': {
            const expected = config?.meterNumber;
            const detected = payloadResult?.meterNumber;

            const resultMeterRates = payloadResult?.meterRates?.[0];

            return (
                <ConfirmDialog
                    closable={false}
                    open={true}
                    showIcon={true}
                    type={ConfirmContentType.WARNING}
                    onCancel={() => onCancel()}
                    title={t(resolveRemoteResultAiTextKey(imageSource, 'invalidMeterNumberOutcome', 'title'))}
                    content={
                        <div className={'text-main'}>
                            <div>{t(resolveRemoteResultAiTextKey(imageSource, 'invalidMeterNumberOutcome', 'description'))}</div>
                        </div>
                    }
                    customContent={
                        <div className={'text-main'}>
                            <table className="border-collapse w-full text-inherit">
                                <tbody>
                                    <tr>
                                        <td className="pr-2 text-inherit align-text-top">
                                            {t(resolveRemoteResultAiTextKey(imageSource, 'invalidMeterNumberOutcome', 'expectedLabel'))}
                                        </td>
                                        <td className="text-inherit">
                                            <div>{expected || '-'}</div>
                                            {configRate?.obis && <div>{MeterHelper.shortenObis(configRate?.obis)}</div>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="pr-2 text-inherit align-text-top">
                                            {t(resolveRemoteResultAiTextKey(imageSource, 'invalidMeterNumberOutcome', 'detectedLabel'))}
                                        </td>
                                        <td className="text-inherit">
                                            <div>{detected || '-'}</div>
                                            {resultMeterRates?.obis && <div>{MeterHelper.shortenObis(resultMeterRates.obis)}</div>}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    }
                    customActions={[
                        <div className={'flex flex-col w-full'} key={'custom-actions'}>
                            <Button ghost={true} block onClick={() => onCancel()}>
                                {t('ai.buttons.cancel')}
                            </Button>
                            <Button
                                ghost={true}
                                block
                                onClick={() => onConfirm({ ...RemoteResultHelper.buildForcedResult(payloadResult, config, configRate?.obis), meta }, imageBase64)}
                            >
                                {t(resolveRemoteResultAiButtonTextKey(imageSource, 'takeImage'))}
                            </Button>
                            <Button primary block onClick={() => onRetakeImage()}>
                                {t(resolveRemoteResultAiButtonTextKey(imageSource, 'retakeImage'))}
                            </Button>
                        </div>,
                    ]}
                />
            );
        }
        case 'INVALID_OBIS': {
            const expected = MeterHelper.shortenObis(expectedObis);

            const detectedObis = payloadResult?.meterRates?.[0]?.obis;
            const detected = MeterHelper.shortenObis(detectedObis);

            const otherMeterRates = config.otherMeterRates;
            const isDetectedObisValid = otherMeterRates?.find((mr) => MeterHelper.shortenObis(mr.obis) === detected) != null;

            return (
                <ConfirmDialog
                    closable={false}
                    open={true}
                    showIcon={true}
                    type={ConfirmContentType.WARNING}
                    onCancel={() => onCancel()}
                    title={t(resolveRemoteResultAiTextKey(imageSource, 'invalidObisOutcome', 'title'))}
                    content={
                        <div className={'text-main'}>
                            <div>{t(resolveRemoteResultAiTextKey(imageSource, 'invalidObisOutcome', 'description'))}</div>
                            <br />
                            {payloadResult?.detectedDisplayCropBase64 && (
                                <div className={'w-full border rounded px-2 py-1 bg-gray-100 mb-2'}>
                                    <HorizontalBase64Image
                                        src={payloadResult?.detectedDisplayCropBase64}
                                        alt={'Fotoausschnitt vom Display'}
                                        className={'rounded'}
                                    />
                                </div>
                            )}
                            <table className="border-collapse w-full text-inherit">
                                <tbody>
                                    <tr>
                                        <td className="pr-2 text-inherit">{t(resolveRemoteResultAiTextKey(imageSource, 'invalidObisOutcome', 'expectedLabel'))}</td>
                                        <td className="text-inherit">{expected || '-'}</td>
                                    </tr>
                                    <tr>
                                        <td className="pr-2 text-inherit">{t(resolveRemoteResultAiTextKey(imageSource, 'invalidObisOutcome', 'detectedLabel'))}</td>
                                        <td className="text-inherit">{detected || '-'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    }
                    customActions={[
                        <div className={'flex flex-col w-full'} key={'custom-actions'}>
                            <Button ghost={true} block onClick={() => onRetakeImage()}>
                                {t(resolveRemoteResultAiButtonTextKey(imageSource, 'retakeImage'))}
                            </Button>
                            <Button
                                ghost={true}
                                block
                                onClick={() => onConfirm({ ...RemoteResultHelper.buildForcedResult(payloadResult, config, expectedObis), meta }, imageBase64)}
                            >
                                {t('ai.buttons.forceObis', { obis: expected || '-' })}
                            </Button>
                            {isDetectedObisValid && (
                                <Button
                                    primary={true}
                                    block
                                    onClick={() => onConfirm({ ...RemoteResultHelper.buildForcedResult(payloadResult, config, detectedObis), meta }, imageBase64)}
                                >
                                    {t('ai.buttons.forceObis', { obis: detected || '-' })}
                                </Button>
                            )}
                        </div>,
                    ]}
                />
            );
        }
        default:
            return null;
    }
};

export default RemoteResultOutcomeGate;
